import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Info, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

type AlertKind = 'info' | 'good' | 'bad' | 'warn';

interface AlertProps {
  kind?: AlertKind;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
}

const kindStyles: Record<AlertKind, { bg: string; border: string; text: string; icon: ReactNode }> =
  {
    info: {
      bg: 'bg-[var(--color-accent-tint)]',
      border: 'border-[var(--color-accent)]',
      text: 'text-[var(--color-accent-ink)]',
      icon: <Info className="h-5 w-5" />,
    },
    good: {
      bg: 'bg-[var(--color-good-bg)]',
      border: 'border-[var(--color-good)]',
      text: 'text-[var(--color-good)]',
      icon: <CheckCircle className="h-5 w-5" />,
    },
    bad: {
      bg: 'bg-[var(--color-bad-bg)]',
      border: 'border-[var(--color-bad)]',
      text: 'text-[var(--color-bad)]',
      icon: <XCircle className="h-5 w-5" />,
    },
    warn: {
      bg: 'bg-[var(--color-warn-bg)]',
      border: 'border-[var(--color-warn)]',
      text: 'text-[var(--color-warn)]',
      icon: <AlertTriangle className="h-5 w-5" />,
    },
  };

export const Alert = ({ kind = 'info', onClose, children, className = '' }: AlertProps) => {
  const { t } = useTranslation();
  const style = kindStyles[kind];

  return (
    <div
      className={
        `flex items-start gap-3 rounded-lg border px-4 py-3 ` +
        `${style.bg} ${style.border} ${style.text} ${className}`
      }
      role="alert"
    >
      <span className="shrink-0 mt-0.5">{style.icon}</span>
      <div className="flex-1 text-sm">{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          className="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100 transition-opacity"
          type="button"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
